<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import { CustomAttributionControl } from '@/utils/customAttribution'

const config = useRuntimeConfig()
const loadedMap = shallowRef(null)
const mapContainer = ref(null)
const { sourceGeojson, clusterGeojson, zoomLevel } = useCameraCluster()
const mapZoom = useState('mapZoom', () => 4)
const mapCenter = useState('mapCenter', () => [137.0, 38.5])

watch(() => clusterGeojson.value, (newValue) => {
  if (!loadedMap.value) return
  const map = loadedMap.value
  const source = map.getSource('liveCamCollection')
  source.setData(newValue)
})

const fetchImage = async (url) => {
  try {
    const res = await axios.get(url, { responseType: 'arraybuffer' })
    if (res.status !== 200) {
      throw new Error('Failed to fetch image')
    }

    const blob = new Blob([res.data], { type: 'image/webp' })
    const image = await createImageBitmap(blob)
    return image
  } catch (error) {
    console.error(error)
    throw error
  }
}

const initMap = () => {
  return new Promise((resolve) => {
    let attributionControl = null
    const protocol = new Protocol()
    maplibregl.addProtocol('pmtiles', protocol.tile)
    const map = new maplibregl.Map({
      container: mapContainer.value,
      style: `${config.public.baseMapDomain}/style/global/osm/basic-ja.json`,
      localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
      center: mapCenter.value,
      zoom: mapZoom.value,
      minZoom: 3,
      maxZoom: 14,
      attributionControl: false,
      doubleClickZoom: false,
      dragRotate: false,
      keyboard: false,
      touchPitch: false
    })
    map.touchZoomRotate.disableRotation()
    map.addControl(new maplibregl.NavigationControl({
      showCompass: false,
      showZoom: true
    }), 'top-right')
    attributionControl = new CustomAttributionControl({
      compact: true
    })
    map.addControl(attributionControl, 'bottom-left')
    map.once('load', async () => {
      zoomLevel.value = map.getZoom()
      map.addSource('liveCamCollection', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        },
        cluster: false,
        generateId: true
      })
      map.addLayer({
        id: 'liveCamThumbCollection',
        source: 'liveCamCollection',
        type: 'symbol',
        layout: {
          'icon-image': ['case', ['==', ['get', 'thumbUrl'], ''], '', ['get', 'thumbUrl']],
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3,
            0.125,
            11,
            0.33
          ],
          'icon-allow-overlap': true
        }
      })
      map.on('styleimagemissing', (e) => {
        const url = e.id
        map.addImage(url, { width: 0, height: 0, data: new Uint8Array() })
        fetchImage(url).then((image) => {
          map.removeImage(url)
          map.addImage(url, image)
          return image
        }).catch((error) => {
          console.error(`Failed to add image for ${url}:`, error)
        })
      })
      map.on('moveend', () => {
        const center = map.getCenter()
        const zoom = map.getZoom()
        mapCenter.value = [center.lng, center.lat]
        mapZoom.value = zoom
      })
      map.on('zoomend', () => {
        zoomLevel.value = map.getZoom()
      })
      map.on('mousemove', 'liveCamThumbCollection', () => {
        map.getCanvas().style.cursor = 'pointer'
      })

      map.on('mouseleave', 'liveCamThumbCollection', () => {
        map.getCanvas().style.cursor = ''
      })

      map.on('click', 'liveCamThumbCollection', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['liveCamThumbCollection']
        })
        if (features.length > 0) {
          const link = features[0].properties.link
          if (link) {
            navigateTo(link)
          }
        }
      })
      const osmSource = map.getSource('osm')
      const attribution = osmSource.attribution
      if (attribution) {
        attributionControl.updateCustomAttributions([attribution])
      }
      loadedMap.value = map
      resolve()
    })
  })
}
onMounted(async () => {
  const [, geojson] = await Promise.all([
    initMap(),
    $fetch('/api/livecam/geojson')
  ])
  sourceGeojson.value = geojson
})
</script>

<template>
  <section class="zoomMap">
    <div class="head">
      <CommonPageTitle title="ライブカメラマップ" />
    </div>
    <div
      ref="mapContainer"
      class="map-container"
    />
  </section>
</template>

<style lang="scss" scoped>
.zoomMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .map-container {
    width: 100%;
    aspect-ratio: 1510 / 1241;
  }
}
</style>
